/**
 * Web app root component.
 *
 * Provides auth, router, Zero sync, and observability.
 *
 * Auth uses the "guaranteed user" pattern via AuthGate:
 * - AuthCookieProvider initializes auth (runs effects)
 * - AuthGate suspends until user is available
 * - Components can safely assume user is never null
 */

import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, Suspense } from "react";

import { AuthCookieProvider } from "@animus/frontend-features/auth-provider-cookie";
import { AuthGate } from "@animus/frontend-features/auth-store";
import { AuthenticatedZeroProvider } from "@animus/frontend-features/auth-zero";
import { ApiConfigProvider } from "@animus/frontend-features/config";
import { PrimarisProvider } from "@animus/primaris";

import { webAuthCache, webCookieAdapter } from "./auth/adapter";
import { ENVIRONMENT } from "./environment";
import { PostHogProvider } from "./observability/posthog";
import { SentryProvider } from "./observability/sentry";
import { router } from "./router/router";

function AuthLoadingFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
      }}
    />
  );
}

export function App() {
  return (
    <StrictMode>
      <PrimarisProvider mode="dark">
        <SentryProvider>
          <PostHogProvider>
            <ApiConfigProvider baseUrl={ENVIRONMENT.API_BASE_URL}>
              <AuthCookieProvider
                adapter={webCookieAdapter}
                cache={webAuthCache}
              >
                <Suspense fallback={<AuthLoadingFallback />}>
                  <AuthGate>
                    <AuthenticatedZeroProvider
                      zeroUrl={ENVIRONMENT.ZERO_CACHE_URL}
                    >
                      <RouterProvider router={router} />
                    </AuthenticatedZeroProvider>
                  </AuthGate>
                </Suspense>
              </AuthCookieProvider>
            </ApiConfigProvider>
          </PostHogProvider>
        </SentryProvider>
      </PrimarisProvider>
    </StrictMode>
  );
}
